<script setup lang="ts">
import { useId } from 'vue'

import RemoveIcon from '../../assets/icons/remove.svg?component'

export type ChipVariant = 'default' | 'danger'

withDefaults(defineProps<{ variant?: ChipVariant; closable?: boolean }>(), {
  variant: 'default',
  closable: false,
})

defineEmits<{ close: [] }>()

defineSlots<{ default(): unknown; icon?(): unknown }>()

const labelId = useId()
const closeId = useId()
</script>

<template>
  <span :class="['chip', `chip--${variant}`]">
    <span v-if="$slots.icon" class="chip__icon"><slot name="icon" /></span>
    <span :id="labelId" class="chip__label"><slot /></span>
    <button
      v-if="closable"
      :id="closeId"
      type="button"
      class="chip__close"
      aria-label="Remove"
      :aria-labelledby="`${closeId} ${labelId}`"
      @click.stop="$emit('close')"
    >
      <RemoveIcon />
    </button>
  </span>
</template>

<style scoped lang="scss">
.chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 6px 8px;
  border: 1px solid var(--button-hover);
  border-radius: 8px;
  background: var(--inverted);
  color: var(--primary);
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

.chip__label {
  font-weight: 600;
}

.chip--danger {
  border-color: var(--error);
  color: var(--error);

  .chip__icon :deep(path) {
    fill: currentColor;
  }
}

.chip__icon,
.chip__close {
  display: inline-flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.chip__close {
  :deep(path) {
    fill: currentColor;
  }

  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
}
</style>
